import { ChangeDetectionStrategy, Component, computed, inject, output } from '@angular/core';
import { TuiButton, TuiDialogService, TuiLoader, TuiNotification } from '@taiga-ui/core';
import { TUI_CONFIRM, TuiAvatar, TuiBadge, TuiConfirmData, TuiToastService } from '@taiga-ui/kit';
import { switchMap } from 'rxjs';
import { OrgUserStore } from '@domains/org-user/org-user.store';
import { AuthStore } from '@domains/auth/auth.store';
import { PermissionManagementService } from '@core/services/permission-management.service';
import { IOrgUser } from '@domains/org-user/org-user.model';

@Component({
  selector: 'app-settings-user-list',
  templateUrl: 'user-list.html',
  styleUrl: 'user-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TuiButton, TuiAvatar, TuiBadge, TuiLoader, TuiNotification],
})
export class UserListComponent {
  private readonly store = inject(OrgUserStore);
  private readonly authStore = inject(AuthStore);
  private readonly permissions = inject(PermissionManagementService);
  private readonly dialogs = inject(TuiDialogService);
  private readonly toasts = inject(TuiToastService);

  readonly add = output<void>();
  readonly edit = output<number>();

  protected readonly users = computed(() =>
    this.store.users().map((user) => ({ ...user, fullName: fullName(user) })),
  );
  protected readonly isLoading = computed(
    () => this.store.isLoading() || this.permissions.isLoading(),
  );
  protected readonly currentUserId = computed(() => this.authStore.currentUser()?.id ?? null);

  protected readonly canList = computed(() => this.permissions.can('admin_users_list'));
  protected readonly canCreate = computed(() => this.permissions.can('admin_users_create'));
  protected readonly canEdit = computed(() => this.permissions.can('admin_users_update'));
  protected readonly canDelete = computed(() => this.permissions.can('admin_users_delete'));

  protected onDelete(user: IOrgUser): void {
    const data: TuiConfirmData = {
      content: `Do you want to delete ${fullName(user)}?`,
      yes: 'Delete',
      no: 'Cancel',
      appearance: 'destructive',
    };

    this.dialogs
      .open<boolean>(TUI_CONFIRM, { label: 'Delete user', size: 's', data })
      .pipe(switchMap((confirmed) => (confirmed ? this.store.deleteUser(user.id) : [])))
      .subscribe({
        next: () =>
          this.toasts.open('User deleted successfully.', { appearance: 'positive' }).subscribe(),
        error: () =>
          this.toasts.open('Failed to delete user.', { appearance: 'negative' }).subscribe(),
      });
  }
}

function fullName(user: IOrgUser): string {
  return `${user.first_name} ${user.last_name}`.trim();
}
